import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useLang } from "@/i18n";
import { api, fmtErr, fmtUsd, fmtDate, fileToBase64, dataUrl } from "@/lib/api";
import {
  Users, UserPlus, Inbox, CheckCircle2, XCircle, Send, Paperclip, Pencil,
  Trash2, FileText, ArrowDownToLine, ArrowUpFromLine, Bell, Newspaper, Image as ImageIcon,
} from "lucide-react";

const inputCls = "mt-1.5 bg-white/5 border-white/10 text-white";
const labelCls = "text-slate-300 text-xs";
const thCls = "text-left px-4 py-3 text-xs uppercase tracking-wider text-slate-500 border-b border-white/5";

function MiniAtt({ att }) {
  if (!att) return null;
  if (att.mime?.startsWith("image/"))
    return <img src={dataUrl(att.mime, att.data)} alt={att.name} className="mt-2 rounded-lg max-w-[180px] border border-white/10" />;
  return (
    <a href={dataUrl(att.mime, att.data)} download={att.name} className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-cyan-400 underline">
      <FileText size={12} /> {att.name}
    </a>
  );
}

// ---------------- CRM ----------------

export function CrmPanel() {
  const { t } = useLang();
  const [data, setData] = useState({ clients: [], leads: [] });
  const load = () => api.get("/admin/crm").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-8" data-testid="admin-crm">
      <div className="glass rounded-2xl overflow-hidden overflow-x-auto">
        <div className="px-5 pt-4 pb-1 flex items-center gap-2">
          <Users size={16} className="text-emerald-400" />
          <h3 className="font-display text-sm font-semibold text-white">{t("admin.clients")} ({data.clients.length})</h3>
        </div>
        <table className="w-full text-sm min-w-[1000px]" data-testid="crm-clients-table">
          <thead>
            <tr>
              <th className={thCls}>{t("admin.client")}</th>
              <th className={thCls}>{t("admin.source")}</th>
              <th className={thCls}>{t("admin.registeredAt")}</th>
              <th className={thCls}>{t("admin.ip")}</th>
              <th className={thCls}>{t("dash.status")}</th>
              <th className={`${thCls} text-right`}>{t("admin.totalValue")}</th>
              <th className={`${thCls} text-right`}>{t("admin.trades")}</th>
              <th className={`${thCls} text-right`}>{t("admin.messages")}</th>
            </tr>
          </thead>
          <tbody>
            {data.clients.map((c) => (
              <tr key={c.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`crm-client-${c.email}`}>
                <td className="px-4 py-3">
                  <p className="font-semibold text-white text-sm">{c.full_name}</p>
                  <p className="text-xs text-slate-500">{c.email} · {c.phone || "—"}</p>
                </td>
                <td className="px-4 py-3 text-xs text-slate-400">{c.created_by === "admin" ? "Admin" : "Self"}</td>
                <td className="px-4 py-3 text-xs font-ticker text-slate-400">{fmtDate(c.created_at)}</td>
                <td className="px-4 py-3 text-xs font-ticker text-cyan-400">{c.registration_ip || "—"}</td>
                <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                <td className="px-4 py-3 text-right font-ticker text-white">{fmtUsd(c.total_value_usd ?? 0)}</td>
                <td className="px-4 py-3 text-right font-ticker text-slate-300">{c.trade_count}</td>
                <td className="px-4 py-3 text-right font-ticker text-slate-300">{c.message_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="glass rounded-2xl overflow-hidden overflow-x-auto">
        <div className="px-5 pt-4 pb-1 flex items-center gap-2">
          <UserPlus size={16} className="text-amber-400" />
          <h3 className="font-display text-sm font-semibold text-white">{t("admin.leads")} ({data.leads.length})</h3>
        </div>
        <table className="w-full text-sm min-w-[800px]" data-testid="crm-leads-table">
          <thead>
            <tr>
              <th className={thCls}>{t("auth.fullName")}</th>
              <th className={thCls}>{t("auth.email")}</th>
              <th className={thCls}>{t("auth.phone")}</th>
              <th className={thCls}>{t("admin.motivation")}</th>
              <th className={thCls}>{t("admin.registeredAt")}</th>
            </tr>
          </thead>
          <tbody>
            {data.leads.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-500">—</td></tr>
            ) : data.leads.map((l) => (
              <tr key={l.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`crm-lead-${l.email}`}>
                <td className="px-4 py-3 text-sm font-semibold text-white">{l.first_name} {l.last_name}</td>
                <td className="px-4 py-3 text-xs text-slate-400">{l.email}</td>
                <td className="px-4 py-3 text-xs text-slate-400">{l.phone}</td>
                <td className="px-4 py-3 text-xs text-slate-400 max-w-[300px] truncate">{l.last_message}</td>
                <td className="px-4 py-3 text-xs font-ticker text-slate-400">{fmtDate(l.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------- Inbox (contact messages) ----------------

export function InboxPanel({ reload }) {
  const { t } = useLang();
  const [messages, setMessages] = useState([]);
  const load = () => api.get("/admin/inbox").then((r) => setMessages(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    try { await api.post(`/admin/inbox/${id}/read`); load(); } catch (err) { toast.error(fmtErr(err)); }
  };

  return (
    <div className="space-y-4" data-testid="admin-inbox">
      {messages.length === 0 && <p className="text-sm text-slate-500 text-center py-10">—</p>}
      {messages.map((m) => (
        <div key={m.id} data-testid={`inbox-msg-${m.id}`}
             className={`glass rounded-2xl p-5 ${!m.read ? "border-l-4 border-l-[#8B5CF6]" : ""}`}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <Inbox size={15} className="text-[#8B5CF6]" />
              <span className="text-sm font-semibold text-white">{m.first_name} {m.last_name}</span>
              <span className={`inline-block px-2 py-0.5 rounded-full border text-[11px] font-medium ${
                m.is_client
                  ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
                  : "bg-amber-500/10 text-amber-400 border-amber-500/30"
              }`}>
                {m.is_client ? t("admin.existingClient") : t("admin.firstContact")}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-[11px] font-ticker text-slate-500">{fmtDate(m.created_at)}</span>
              {!m.read && (
                <Button size="sm" variant="ghost" data-testid={`inbox-read-${m.id}`} onClick={() => markRead(m.id)}
                        className="h-7 px-2 text-xs text-emerald-400 hover:bg-emerald-500/10">
                  <CheckCircle2 size={13} className="mr-1" /> {t("admin.markRead")}
                </Button>
              )}
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-2">{m.email} · {m.phone} · IP {m.ip}</p>
          <p className="text-sm text-slate-200 mt-2 whitespace-pre-line leading-relaxed">{m.message}</p>
        </div>
      ))}
    </div>
  );
}

// ---------------- Chat ----------------

export function ChatPanel() {
  const { t } = useLang();
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState([]);
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editText, setEditText] = useState("");
  const fileRef = useRef(null);
  const bottomRef = useRef(null);

  const loadConvos = () => api.get("/admin/chat").then((r) => setConvos(r.data)).catch(() => {});
  const loadThread = (uid) => api.get(`/admin/chat/${uid}`).then((r) => setThread(r.data)).catch(() => {});

  useEffect(() => {
    loadConvos();
    const iv = setInterval(() => { loadConvos(); if (active) loadThread(active); }, 5000);
    return () => clearInterval(iv);
  }, [active]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread]);

  const send = async (e) => {
    e.preventDefault();
    if (!active || (!text.trim() && !file)) return;
    try {
      let attachment = null;
      if (file) attachment = await fileToBase64(file);
      await api.post(`/admin/chat/${active}`, { text: text.trim(), attachment });
      setText(""); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      loadThread(active);
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const saveEdit = async (id) => {
    try {
      await api.patch(`/admin/chat/${id}`, { text: editText });
      setEditing(null);
      loadThread(active);
      toast.success(t("admin.tradeSaved"));
    } catch (err) { toast.error(fmtErr(err)); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-5" data-testid="admin-chat">
      <div className="glass rounded-2xl p-4 max-h-[560px] overflow-y-auto">
        <h3 className="font-display text-sm font-semibold text-white mb-3">Conversations</h3>
        {convos.length === 0 && <p className="text-xs text-slate-500 py-6 text-center">{t("admin.noConversations")}</p>}
        {convos.map((c) => (
          <button key={c.user_id} data-testid={`convo-${c.user_id}`}
                  onClick={() => { setActive(c.user_id); loadThread(c.user_id); }}
                  className={`w-full text-left rounded-xl p-3 mb-2 transition-colors border ${
                    active === c.user_id ? "border-emerald-500/40 bg-emerald-500/10" : "border-white/5 hover:bg-white/5"
                  }`}>
            <p className="text-sm font-semibold text-white truncate">{c.full_name || c.user_email}</p>
            <p className="text-[11px] text-slate-500 truncate mt-0.5">
              {(c.last_message.sender === "admin" ? "Desk: " : "") + (c.last_message.auto ? "auto-reply" : c.last_message.text || "📎")}
            </p>
            <p className="text-[10px] font-ticker text-slate-600 mt-1">{fmtDate(c.last_message.created_at)}</p>
          </button>
        ))}
      </div>

      <div className="lg:col-span-2 glass rounded-2xl flex flex-col h-[560px]">
        {!active ? (
          <p className="text-sm text-slate-500 text-center my-auto">{t("admin.selectClient")}</p>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="admin-chat-thread">
              {thread.map((m) => {
                const mine = m.sender === "admin";
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-3.5 py-2.5 text-sm ${
                      mine ? "bg-emerald-600/80 text-white rounded-br-md" : "bg-white/5 border border-white/10 text-slate-200 rounded-bl-md"
                    } ${m.auto ? "italic opacity-80" : ""}`}>
                      {editing === m.id ? (
                        <div className="flex items-center gap-2">
                          <Input value={editText} onChange={(e) => setEditText(e.target.value)}
                                 className="h-8 bg-white/10 border-white/20 text-white text-xs" data-testid="chat-edit-input" />
                          <Button size="sm" className="h-8 bg-emerald-600 text-white" data-testid="chat-edit-save"
                                  onClick={() => saveEdit(m.id)}>{t("common.save")}</Button>
                        </div>
                      ) : (
                        <p className="whitespace-pre-line">{m.auto ? t("chat.autoReply") : m.text}</p>
                      )}
                      <MiniAtt att={m.attachment} />
                      <div className={`flex items-center gap-2 text-[9px] mt-1 font-ticker ${mine ? "text-white/60" : "text-slate-500"}`}>
                        <span>{fmtDate(m.created_at)} {m.edited ? t("chat.edited") : ""}</span>
                        {mine && !m.auto && editing !== m.id && (
                          <button onClick={() => { setEditing(m.id); setEditText(m.text); }}
                                  data-testid={`chat-edit-btn-${m.id}`} className="hover:text-white" title={t("admin.editMessage")}>
                            <Pencil size={10} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>
            <form onSubmit={send} className="p-3 border-t border-white/5">
              {file && <p className="text-xs text-cyan-400 mb-2 flex items-center gap-1.5"><Paperclip size={12} /> {file.name}</p>}
              <div className="flex items-center gap-2">
                <input ref={fileRef} type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                <button type="button" onClick={() => fileRef.current?.click()} data-testid="admin-chat-attach"
                        className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-slate-400 hover:text-white shrink-0">
                  <Paperclip size={15} />
                </button>
                <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={t("admin.typeMessage")}
                       className="flex-1 h-9 bg-white/5 border-white/10 text-white rounded-full px-4" data-testid="admin-chat-input" />
                <Button type="submit" size="sm" data-testid="admin-chat-send"
                        className="h-9 w-9 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white p-0 shrink-0">
                  <Send size={15} />
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------- Notifications ----------------

export function NotificationsPanel({ clients }) {
  const { t } = useLang();
  const [userId, setUserId] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [sent, setSent] = useState([]);
  const load = () => api.get("/admin/notifications").then((r) => setSent(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/notifications", { user_id: userId, title, body });
      toast.success(t("admin.statusUpdated"));
      setTitle(""); setBody("");
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="admin-notifications">
      <form onSubmit={submit} className="glass rounded-2xl p-6 space-y-4" data-testid="notification-form">
        <h3 className="font-display text-sm font-semibold text-white flex items-center gap-2">
          <Bell size={15} className="text-[#8B5CF6]" /> {t("admin.sendNotification")}
        </h3>
        <div>
          <Label className={labelCls}>{t("admin.client")}</Label>
          <Select value={userId} onValueChange={setUserId} required>
            <SelectTrigger data-testid="notif-client-select" className={inputCls}><SelectValue /></SelectTrigger>
            <SelectContent>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.full_name} — {c.email}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className={labelCls}>{t("admin.notifTitle")}</Label>
          <Input data-testid="notif-title-input" value={title} onChange={(e) => setTitle(e.target.value)} required className={inputCls} />
        </div>
        <div>
          <Label className={labelCls}>{t("admin.notifBody")}</Label>
          <Textarea data-testid="notif-body-input" rows={4} value={body} onChange={(e) => setBody(e.target.value)} required
                    className={`${inputCls} resize-none`} />
        </div>
        <Button data-testid="notif-send-btn" type="submit" className="w-full bg-emerald-600 hover:bg-emerald-500 text-white">
          {t("admin.sendNotification")}
        </Button>
      </form>
      <div className="glass rounded-2xl p-6 max-h-[520px] overflow-y-auto">
        <h3 className="font-display text-sm font-semibold text-white mb-4">{t("admin.notificationsTab")}</h3>
        <div className="space-y-3">
          {sent.map((n) => (
            <div key={n.id} className="border border-white/8 rounded-xl p-3.5 bg-white/[0.02]" data-testid={`sent-notif-${n.id}`}>
              <p className="text-sm font-semibold text-white">{n.title}</p>
              <p className="text-xs text-slate-400 mt-1 whitespace-pre-line">{n.body}</p>
              <p className="text-[10px] font-ticker text-slate-500 mt-1.5">
                {clients.find((c) => c.id === n.user_id)?.email || n.user_id} · {fmtDate(n.created_at)} · {n.read ? "✓ read" : "unread"}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------- Documents ----------------

export function DocumentsPanel() {
  const { t } = useLang();
  const [docs, setDocs] = useState([]);
  const load = () => api.get("/admin/documents").then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try { await api.post(`/admin/documents/${id}/status`, { status }); toast.success(t("admin.statusUpdated")); load(); }
    catch (err) { toast.error(fmtErr(err)); }
  };

  const download = async (id) => {
    try {
      const { data } = await api.get(`/admin/documents/${id}/file`);
      const a = document.createElement("a");
      a.href = dataUrl(data.mime, data.data);
      a.download = data.filename;
      a.click();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  return (
    <div className="glass rounded-2xl overflow-hidden overflow-x-auto" data-testid="admin-documents">
      <table className="w-full text-sm min-w-[860px]" data-testid="admin-documents-table">
        <thead>
          <tr>
            <th className={thCls}>{t("dash.time")}</th>
            <th className={thCls}>{t("admin.client")}</th>
            <th className={thCls}>{t("admin.type")}</th>
            <th className={thCls}>File</th>
            <th className={thCls}>{t("dash.status")}</th>
            <th className={`${thCls} text-right`}>{t("admin.actions")}</th>
          </tr>
        </thead>
        <tbody>
          {docs.map((d) => (
            <tr key={d.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`doc-row-${d.id}`}>
              <td className="px-4 py-3 text-xs font-ticker text-slate-400">{fmtDate(d.created_at)}</td>
              <td className="px-4 py-3 text-xs text-slate-300">{d.user_email}</td>
              <td className="px-4 py-3 text-xs text-slate-300 uppercase">{d.category}</td>
              <td className="px-4 py-3">
                <button onClick={() => download(d.id)} data-testid={`doc-download-${d.id}`}
                        className="text-xs text-cyan-400 hover:underline font-ticker max-w-[220px] truncate inline-block align-middle">
                  {d.filename}
                </button>
              </td>
              <td className="px-4 py-3"><StatusBadge status={d.status} /></td>
              <td className="px-4 py-3">
                {d.status === "pending" && (
                  <div className="flex items-center justify-end gap-1.5">
                    <Button size="sm" variant="ghost" data-testid={`doc-validate-${d.id}`} onClick={() => setStatus(d.id, "validated")}
                            className="h-8 px-2 text-emerald-400 hover:bg-emerald-500/10"><CheckCircle2 size={15} /></Button>
                    <Button size="sm" variant="ghost" data-testid={`doc-reject-${d.id}`} onClick={() => setStatus(d.id, "rejected")}
                            className="h-8 px-2 text-red-400 hover:bg-red-500/10"><XCircle size={15} /></Button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------- Transactions (deposits / withdrawals) ----------------

export function TransactionsPanel() {
  const { t } = useLang();
  const [txs, setTxs] = useState([]);
  const load = () => api.get("/admin/transactions").then((r) => setTxs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try { await api.post(`/admin/transactions/${id}/status`, { status }); toast.success(t("admin.statusUpdated")); load(); }
    catch (err) { toast.error(fmtErr(err)); }
  };

  return (
    <div className="glass rounded-2xl overflow-hidden overflow-x-auto" data-testid="admin-transactions">
      <table className="w-full text-sm min-w-[900px]" data-testid="admin-transactions-table">
        <thead>
          <tr>
            <th className={thCls}>{t("dash.time")}</th>
            <th className={thCls}>{t("admin.client")}</th>
            <th className={thCls}>{t("admin.type")}</th>
            <th className={thCls}>{t("admin.mode")}</th>
            <th className={`${thCls} text-right`}>{t("admin.amount")}</th>
            <th className={thCls}>{t("admin.details")}</th>
            <th className={thCls}>{t("dash.status")}</th>
            <th className={`${thCls} text-right`}>{t("admin.actions")}</th>
          </tr>
        </thead>
        <tbody>
          {txs.map((tx) => (
            <tr key={tx.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`tx-row-${tx.id}`}>
              <td className="px-4 py-3 text-xs font-ticker text-slate-400">{fmtDate(tx.created_at)}</td>
              <td className="px-4 py-3 text-xs text-slate-300">{tx.user_email}</td>
              <td className="px-4 py-3">
                <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${tx.kind === "deposit" ? "text-emerald-400" : "text-pink-400"}`}>
                  {tx.kind === "deposit" ? <ArrowDownToLine size={13} /> : <ArrowUpFromLine size={13} />}
                  {tx.kind.toUpperCase()} · {tx.asset}
                </span>
              </td>
              <td className="px-4 py-3 text-xs text-slate-400">{tx.method === "bank" ? "SEPA" : "Crypto"}</td>
              <td className="px-4 py-3 text-right font-ticker text-white">{tx.asset === "USD" ? fmtUsd(tx.amount) : `${tx.amount} ${tx.asset}`}</td>
              <td className="px-4 py-3 text-xs text-slate-400 max-w-[240px] truncate">{tx.details || "—"}</td>
              <td className="px-4 py-3"><StatusBadge status={tx.status} /></td>
              <td className="px-4 py-3">
                {tx.status === "pending" && (
                  <div className="flex items-center justify-end gap-1.5">
                    <Button size="sm" variant="ghost" data-testid={`tx-approve-${tx.id}`} onClick={() => setStatus(tx.id, "approved")}
                            className="h-8 px-2 text-emerald-400 hover:bg-emerald-500/10"><CheckCircle2 size={15} /></Button>
                    <Button size="sm" variant="ghost" data-testid={`tx-reject-${tx.id}`} onClick={() => setStatus(tx.id, "rejected")}
                            className="h-8 px-2 text-red-400 hover:bg-red-500/10"><XCircle size={15} /></Button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------- Fiscal ----------------

const FISCAL_TAB_LABELS = { foreign_account: "Foreign account", numbered_account: "Numbered account", optimized_withdrawal: "Optimized withdrawal" };

export function FiscalPanel() {
  const { t } = useLang();
  const [data, setData] = useState({ requests: [], submissions: [] });
  const load = () => api.get("/admin/fiscal").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setReq = async (uid, status) => {
    try { await api.post(`/admin/fiscal/${uid}/status`, { status }); toast.success(t("admin.statusUpdated")); load(); }
    catch (err) { toast.error(fmtErr(err)); }
  };
  const setSub = async (id, status) => {
    try { await api.post(`/admin/fiscal/submissions/${id}/status`, { status }); toast.success(t("admin.statusUpdated")); load(); }
    catch (err) { toast.error(fmtErr(err)); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="admin-fiscal">
      <div className="glass rounded-2xl p-6">
        <h3 className="font-display text-sm font-semibold text-white mb-4">{t("admin.requests")}</h3>
        <div className="space-y-3">
          {data.requests.length === 0 && <p className="text-xs text-slate-500 py-6 text-center">—</p>}
          {data.requests.map((r) => (
            <div key={r.id || r.user_id} className="border border-white/8 rounded-xl p-4 bg-white/[0.02]" data-testid={`fiscal-req-${r.user_id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="text-sm font-semibold text-white">{r.user_email}</span>
                <StatusBadge status={r.status === "active" ? "active" : r.status === "rejected" ? "rejected" : "pending"} />
              </div>
              {r.motivation && <p className="text-xs text-slate-400 mt-2 whitespace-pre-line">{r.motivation}</p>}
              <p className="text-[10px] font-ticker text-slate-500 mt-1.5">{fmtDate(r.created_at)}</p>
              {r.status !== "active" && (
                <div className="flex gap-2 mt-3">
                  <Button size="sm" data-testid={`fiscal-activate-${r.user_id}`} onClick={() => setReq(r.user_id, "active")}
                          className="h-8 bg-emerald-600 hover:bg-emerald-500 text-white text-xs">
                    <CheckCircle2 size={13} className="mr-1" /> {t("admin.activate")}
                  </Button>
                  {r.status !== "rejected" && (
                    <Button size="sm" variant="ghost" data-testid={`fiscal-reject-${r.user_id}`} onClick={() => setReq(r.user_id, "rejected")}
                            className="h-8 text-red-400 hover:bg-red-500/10 text-xs">
                      <XCircle size={13} className="mr-1" /> {t("admin.reject")}
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="glass rounded-2xl p-6">
        <h3 className="font-display text-sm font-semibold text-white mb-4">{t("admin.submissions")}</h3>
        <div className="space-y-3">
          {data.submissions.length === 0 && <p className="text-xs text-slate-500 py-6 text-center">—</p>}
          {data.submissions.map((s) => (
            <div key={s.id} className="border border-white/8 rounded-xl p-4 bg-white/[0.02]" data-testid={`fiscal-sub-${s.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="text-xs font-semibold text-cyan-400">{FISCAL_TAB_LABELS[s.tab] || s.tab}</span>
                <StatusBadge status={s.status} />
              </div>
              <p className="text-xs text-slate-300 mt-2 whitespace-pre-line">{s.content}</p>
              <p className="text-[10px] font-ticker text-slate-500 mt-1.5">{s.user_email} · {fmtDate(s.created_at)}</p>
              {s.status === "pending" && (
                <div className="flex gap-2 mt-3">
                  <Button size="sm" data-testid={`fiscal-sub-validate-${s.id}`} onClick={() => setSub(s.id, "validated")}
                          className="h-8 bg-emerald-600 hover:bg-emerald-500 text-white text-xs">{t("admin.validate")}</Button>
                  <Button size="sm" variant="ghost" data-testid={`fiscal-sub-reject-${s.id}`} onClick={() => setSub(s.id, "rejected")}
                          className="h-8 text-red-400 hover:bg-red-500/10 text-xs">{t("admin.reject")}</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------- News (publish to vitrine) ----------------

export function NewsPanel() {
  const { t } = useLang();
  const [posts, setPosts] = useState([]);
  const emptyForm = { title_en: "", title_fr: "", excerpt_en: "", excerpt_fr: "", body_en: "", body_fr: "", image: "" };
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/news").then((r) => setPosts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const pickImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const b64 = await fileToBase64(f);
    setForm((p) => ({ ...p, image: dataUrl(b64.mime, b64.data) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.patch(`/admin/news/${editing}`, form);
      else await api.post("/admin/news", form);
      toast.success(t("admin.tradeSaved"));
      setForm(emptyForm);
      setEditing(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/admin/news/${id}`); toast.success(t("admin.tradeDeleted")); load(); }
    catch (err) { toast.error(fmtErr(err)); }
  };

  const startEdit = (p) => {
    setEditing(p.id);
    setForm({ title_en: p.title_en, title_fr: p.title_fr, excerpt_en: p.excerpt_en, excerpt_fr: p.excerpt_fr, body_en: p.body_en, body_fr: p.body_fr, image: p.image });
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="admin-news">
      <form onSubmit={submit} className="glass rounded-2xl p-6 space-y-4" data-testid="news-form">
        <h3 className="font-display text-sm font-semibold text-white flex items-center gap-2">
          <Newspaper size={15} className="text-[#8B5CF6]" /> {editing ? t("admin.edit") : t("admin.newsTitle")}
        </h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className={labelCls}>Title EN</Label>
            <Input data-testid="news-title-en" value={form.title_en} required
                   onChange={(e) => setForm({ ...form, title_en: e.target.value })} className={inputCls} />
          </div>
          <div>
            <Label className={labelCls}>Titre FR</Label>
            <Input data-testid="news-title-fr" value={form.title_fr} required
                   onChange={(e) => setForm({ ...form, title_fr: e.target.value })} className={inputCls} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className={labelCls}>Excerpt EN</Label>
            <Textarea data-testid="news-excerpt-en" rows={2} value={form.excerpt_en}
                      onChange={(e) => setForm({ ...form, excerpt_en: e.target.value })} className={`${inputCls} resize-none`} />
          </div>
          <div>
            <Label className={labelCls}>Extrait FR</Label>
            <Textarea data-testid="news-excerpt-fr" rows={2} value={form.excerpt_fr}
                      onChange={(e) => setForm({ ...form, excerpt_fr: e.target.value })} className={`${inputCls} resize-none`} />
          </div>
        </div>
        <div>
          <Label className={labelCls}>Body EN</Label>
          <Textarea data-testid="news-body-en" rows={4} value={form.body_en}
                    onChange={(e) => setForm({ ...form, body_en: e.target.value })} className={`${inputCls} resize-none`} />
        </div>
        <div>
          <Label className={labelCls}>Corps FR</Label>
          <Textarea data-testid="news-body-fr" rows={4} value={form.body_fr}
                    onChange={(e) => setForm({ ...form, body_fr: e.target.value })} className={`${inputCls} resize-none`} />
        </div>
        <div>
          <Label className={labelCls}>{t("admin.image")}</Label>
          <div className="flex items-center gap-3 mt-1.5">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickImage} data-testid="news-image-input" />
            <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()}
                    data-testid="news-image-btn" className="border-white/15 text-slate-300 hover:bg-white/5">
              <ImageIcon size={14} className="mr-1.5" /> {form.image ? "✓" : t("admin.image")}
            </Button>
            {form.image && <img src={form.image} alt="" className="h-10 w-16 object-cover rounded-md border border-white/10" />}
          </div>
        </div>
        <Button data-testid="news-publish-btn" type="submit" className="w-full bg-emerald-600 hover:bg-emerald-500 text-white">
          {editing ? t("common.save") : t("admin.publish")}
        </Button>
        {editing && (
          <Button type="button" variant="ghost" onClick={() => { setEditing(null); setForm(emptyForm); }}
                  className="w-full text-slate-400 hover:text-white">{t("common.cancel")}</Button>
        )}
      </form>

      <div className="glass rounded-2xl p-6 max-h-[640px] overflow-y-auto">
        <h3 className="font-display text-sm font-semibold text-white mb-4">{t("admin.news")}</h3>
        {posts.length === 0 && <p className="text-xs text-slate-500 py-6 text-center">{t("admin.newsEmpty")}</p>}
        <div className="space-y-3">
          {posts.map((p) => (
            <div key={p.id} className="border border-white/8 rounded-xl p-4 bg-white/[0.02] flex gap-3" data-testid={`news-post-${p.id}`}>
              {p.image && <img src={p.image} alt="" className="w-16 h-16 rounded-lg object-cover border border-white/10 shrink-0" />}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-white truncate">{p.title_en} / {p.title_fr}</p>
                <p className="text-xs text-slate-400 truncate mt-0.5">{p.excerpt_en}</p>
                <p className="text-[10px] font-ticker text-slate-500 mt-1">{fmtDate(p.created_at)}</p>
              </div>
              <div className="flex flex-col gap-1.5 shrink-0">
                <Button size="sm" variant="ghost" data-testid={`news-edit-${p.id}`} onClick={() => startEdit(p)}
                        className="h-7 px-2 text-cyan-400 hover:bg-cyan-500/10"><Pencil size={13} /></Button>
                <Button size="sm" variant="ghost" data-testid={`news-delete-${p.id}`} onClick={() => remove(p.id)}
                        className="h-7 px-2 text-red-400 hover:bg-red-500/10"><Trash2 size={13} /></Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
